/**
 * Global reCAPTCHA Manager
 *
 * Handles reCAPTCHA verification globally to avoid multiple simultaneous requests.
 * Implements caching and request deduplication to optimize performance.
 */

class GlobalRecaptchaManager {
  constructor() {
    this.isVerifying = false
    this.verificationPromise = null
    this.isVerified = null
    this.lastVerificationTime = null
    this.verificationCache = new Map()

    // Cache duration: 1 minute
    this.CACHE_DURATION = 60 * 1000

    this.verify = this.verify.bind(this)
    this.isCurrentlyVerified = this.isCurrentlyVerified.bind(this)
    this.clearCache = this.clearCache.bind(this)

    this.setupNavigationListeners()
  }

  setupNavigationListeners() {
    document.addEventListener('turbo:load', () => {
      this.clearCache()

      setTimeout(() => {
        if (!this.isCurrentlyVerified() && !this.isVerifying) {
          this.verify('submit')
        }
      }, 500) // Give React components time to mount
    })

    window.addEventListener('beforeunload', () => {
      this.clearCache()
    })
  }

  /**
   * Get or create a verification for the given action
   * @param {string} action - reCAPTCHA action
   * @returns {Promise<string>} reCAPTCHA token
   */
  async verify(action = 'submit') {
    const cached = this.verificationCache.get(action)
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      return cached.token
    }

    if (this.isVerifying && this.verificationPromise) {
      return this.verificationPromise
    }

    this.isVerifying = true
    this.verificationPromise = GlobalRecaptchaManager.executeVerification(action)

    try {
      const token = await this.verificationPromise

      this.verificationCache.set(action, {
        token,
        timestamp: Date.now(),
      })

      this.isVerified = true
      this.lastVerificationTime = Date.now()

      return token
    } catch (error) {
      this.isVerified = false
      throw error
    } finally {
      this.isVerifying = false
      this.verificationPromise = null
    }
  }

  /**
   * Execute the actual reCAPTCHA verification
   * @param {string} action
   * @returns {Promise<string>}
   */
  static async executeVerification(action) {
    await GlobalRecaptchaManager.waitForRecaptcha()

    if (!window.grecaptcha?.enterprise?.execute) {
      throw new Error('Google reCAPTCHA not available')
    }

    return window.grecaptcha.enterprise.execute(window.grecaptchaHandle, { action })
  }

  static isRecaptchaReady() {
    return window.grecaptchaHandle !== undefined && window.grecaptcha?.enterprise?.execute
  }

  /**
   * Wait for reCAPTCHA to be loaded
   * @returns {Promise<void>}
   */
  static waitForRecaptcha() {
    return new Promise((resolve) => {
      if (GlobalRecaptchaManager.isRecaptchaReady()) {
        resolve()
        return
      }

      const checkLoaded = () => {
        if (GlobalRecaptchaManager.isRecaptchaReady()) {
          resolve()
        } else {
          setTimeout(checkLoaded, 100)
        }
      }

      const handleLoaded = () => {
        window.removeEventListener('recaptchaLoaded', handleLoaded)
        resolve()
      }

      window.addEventListener('recaptchaLoaded', handleLoaded)
      checkLoaded()
    })
  }

  /**
   * Check if we have a current valid verification
   * @returns {boolean}
   */
  isCurrentlyVerified() {
    if (!this.isVerified || !this.lastVerificationTime) {
      return false
    }

    return (Date.now() - this.lastVerificationTime) < this.CACHE_DURATION
  }

  /**
   * Clear all cached verifications
   */
  clearCache() {
    this.verificationCache.clear()
    this.isVerified = null
    this.lastVerificationTime = null
    this.isVerifying = false
    this.verificationPromise = null
  }

  /**
   * Get verification status without triggering new verification
   * @returns {boolean|null} true if verified, false if failed, null if unknown
   */
  getVerificationStatus() {
    return this.isCurrentlyVerified() ? true : null
  }
}

const globalRecaptchaManager = new GlobalRecaptchaManager()

if (typeof window !== 'undefined') {
  window.globalRecaptchaManager = globalRecaptchaManager
}

export default globalRecaptchaManager

